import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import env from '@beam-australia/react-env';
import { RENEWAL_CONFIRMATION, RENEWAL_ERROR } from 'constants/routeConstants';
import { UsePrompt } from 'hooks/routerBlocker';
import { API_GET_PAYMENT_IFRAME } from 'constants/apiConstants';
import paymentResponse from 'constants/paymentServiceConstants';
import { setCardPayment, getCardPaymentStatus } from 'services/RenewalService';
import Spinner from 'components/Spinner';
import { PaymentError } from 'types/expiryRenewal';
import { Store } from 'types/store';

import InfoBlock from '../../common-components/InfoBlock/InfoBlock';
import TechnicalError from '../molecules/TechnicalError';
import VisaSVG from '../../assets/svg/visa.svg'
import MastercardSVG from '../../assets/svg/mastercard.svg'

import '../../expiryRenewalGlobals.scss';

interface Props {
  header: string;
  error?: PaymentError | null;
}

const CardPaymentForm = ({ header, error }: Props) => {
  const [iFrameSrc, setIframeSrc] = useState<React.ReactNode>(<Spinner bootSpinner />);
  const [iFrameError, setIframeError] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);

  const { paymentData } = useSelector((state: Store) => state.expiryRenewal);

  const paymentId = paymentData?.createdPaymentId || null;
  const lockToken = paymentData?.lockToken || null;

  UsePrompt('Changes you made may not be saved.', showPrompt);

  const navigate = useNavigate();

  const paymentIntervalRef = useRef<number | null>(null);
  const isProcessingRef = useRef(false);

  const handleConfirmation = async () => {
    if (paymentIntervalRef.current) {
      clearInterval(paymentIntervalRef.current);
      paymentIntervalRef.current = null;
    }

    const payload = {
      PaymentId: paymentId,
      LockToken: lockToken,
    };

    await setCardPayment(payload).then(() => {
      navigate(RENEWAL_CONFIRMATION, { replace: true });
    });
  };

  const getPaymentStatus = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      const response = await getCardPaymentStatus(paymentId);
      const paymentStatus = response?.payload;

      if (paymentStatus.success && paymentStatus.statusCode === paymentResponse.SUCCESS && !paymentStatus.isAwaitingResponse) {
        // Payment polling service returned success therefore redirect user to confirmation page
        handleConfirmation();
      }

      if (paymentStatus.attempts === 3 && !paymentStatus.success) {
        if (paymentIntervalRef.current) {
          clearInterval(paymentIntervalRef.current);
          paymentIntervalRef.current = null;
        }

        const payload = {
          PaymentId: paymentId,
          LockToken: lockToken,
        };

        await setCardPayment(payload).then(() => {
          navigate(RENEWAL_ERROR, { state: { isPaymentError: true }, replace: true });
        });
      }
    } catch {
      setIframeSrc(<TechnicalError buttonClickHandler={getPaymentForm} />);
      setIframeError(true);
    } finally {
      isProcessingRef.current = false;
    }
  };

  // Event listener waits for a message event to be triggered from the payment response service
  async function listener(event: MessageEvent) {
    if (event.data && event.data.paymentId) {
      setShowPrompt(!(event.data.success || event.data.maxAttempt));
      // Method added to prevent further propagation of the current event for potential bubbling/conflict with javascript from live person chat bot
      event.stopPropagation();
    }
  }

  const getPaymentForm = async () => {
    if (paymentId) {
      fetch(API_GET_PAYMENT_IFRAME(paymentId)).then((response) => {
        if (response?.status === 200) {
          // Payment form
          setIframeSrc(<iframe id="paymentIFrame" title="Payment IFrame" src={API_GET_PAYMENT_IFRAME(paymentId)} width="100%" height="100%" scrolling="no" />);
          setIframeError(false);
        } else {
          // Error, try again
          setIframeSrc(<TechnicalError buttonClickHandler={getPaymentForm} />);
          setIframeError(true);
        }
      }).catch(() => {
        // Error, try again
        setIframeSrc(<TechnicalError buttonClickHandler={getPaymentForm} />);
        setIframeError(true);
      });
    }
  };

  useEffect(() => {
    // Event listener for GlobalPay message response
    window.addEventListener('message', listener);
    return () => {
      window.removeEventListener('message', listener);
    };
  }, [paymentId, navigate]);

  useEffect(() => {
    if (!paymentId) return;

    const interval = Number.parseInt(
      env('PAYMENT_SERVICE_STATUS_INTERVAL_TIME'),
      10,
    );

    const pollingInterval = Number.isFinite(interval) ? interval : 5000;

    paymentIntervalRef.current = window.setInterval(
      getPaymentStatus,
      pollingInterval,
    );

    // eslint-disable-next-line consistent-return
    return () => {
      if (paymentIntervalRef.current) window.clearInterval(paymentIntervalRef.current);
      paymentIntervalRef.current = null;
    };
  }, [paymentId]);

  useEffect(() => {
    // Show payment form or technical error in iFrame
    async function renderIframe() {
      await getPaymentForm();
    }
    if (paymentId) {
      renderIframe();
    } else {
      setIframeSrc(<TechnicalError buttonClickHandler={getPaymentForm} />);
      setIframeError(true);
    }
  }, []);

  return (
    <div className="d-flex justify-content-center border border-1 border-grey rounded-2">
      <div className="vstack gap-1">
        <p className="fw-bold fs-6 p-3 m-0 lh-sm">
          {header}
        </p>
        <div className="mx-3 text-end">
          <img src={VisaSVG} alt="Visa icon" className="mx-2" />
          <img src={MastercardSVG} alt="Mastercard icon" />
        </div>
        {error?.message && error?.title ? (
          <div className="p-4">
            <InfoBlock header={error?.title} variant="danger" showIcon>
              <span className="text-danger">
                {error?.message}
              </span>
            </InfoBlock>
          </div>
        ) : (
          <div className="m-3 rounded">
            <div>
              <div className="iframe-container">
                {iFrameError ? (
                  iFrameSrc
                ) : (
                  <div className="hpp_iframe">
                    { iFrameSrc }
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
};

export default CardPaymentForm;
